import React from 'react';

const Privacy = () => {
  return (
    <div className="pt-32 pb-24 px-6 max-w-3xl mx-auto">
      <div className="mb-12">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-amber-50 mb-4">Privacy Policy</h1>
        <p className="text-zinc-400">Last updated: June 28, 2026</p>
      </div>

      <div className="prose prose-invert prose-orange max-w-none text-zinc-300">
        <p className="mb-6 leading-relaxed">
          Moment of Truth respects your privacy. This Privacy Policy explains what information we collect, how we use it, how we share it, and what choices you have.
        </p>
        <p className="mb-6 leading-relaxed">
          By using Moment of Truth, you agree to the collection and use of information as described in this Privacy Policy.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">1. About Moment of Truth</h2>
        <p className="mb-6 leading-relaxed">
          Moment of Truth helps businesses collect, import, manage, sync, publish, and showcase customer reviews and testimonials through public Wall of Love pages, widgets, badges, and related tools.
        </p>
        <p className="mb-6 leading-relaxed">
          Because our product helps you display review content publicly, some information you add or approve inside Moment of Truth may become publicly visible.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">2. Information We Collect</h2>
        <p className="mb-6 leading-relaxed">
          We collect information needed to provide, secure, and improve Moment of Truth.
        </p>

        <h3 className="text-xl font-semibold text-amber-100 mt-6 mb-3">Account Information</h3>
        <p className="mb-4 leading-relaxed">When you create an account, we may collect:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Your name.</li>
          <li>Your email address.</li>
          <li>Login and authentication details.</li>
          <li>Account preferences.</li>
          <li>Avatar or profile image, if you upload one.</li>
        </ul>

        <h3 className="text-xl font-semibold text-amber-100 mt-6 mb-3">Company and Workspace Information</h3>
        <p className="mb-4 leading-relaxed">
          To help set up your public review page and verify ownership of the reviews you showcase, we may collect:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Company name.</li>
          <li>Company website or domain.</li>
          <li>Company logo.</li>
          <li>Public profile/page username.</li>
          <li>Brand details used on your Wall of Love page or widgets.</li>
          <li>Information about connected review sources.</li>
        </ul>

        <h3 className="text-xl font-semibold text-amber-100 mt-6 mb-3">Review and Testimonial Content</h3>
        <p className="mb-4 leading-relaxed">
          When you import, sync, manually add, approve, or publish reviews, we may process:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Review text.</li>
          <li>Ratings.</li>
          <li>Reviewer names.</li>
          <li>Reviewer job titles or company names, if provided.</li>
          <li>Reviewer profile photos, if provided or imported.</li>
          <li>Review source/platform.</li>
          <li>Review dates.</li>
          <li>Links or metadata related to the original review.</li>
          <li>Manual review submissions.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          This content may be displayed publicly if you choose to publish it on your Wall of Love page or widgets.
        </p>

        <h3 className="text-xl font-semibold text-amber-100 mt-6 mb-3">Usage and Product Data</h3>
        <p className="mb-4 leading-relaxed">
          We collect basic product usage data required to operate the service and enforce plan limits. This may include:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Number of reviews added or synced.</li>
          <li>Number of reviews published.</li>
          <li>Number of widgets created.</li>
          <li>Widget usage and configuration.</li>
          <li>Badge usage.</li>
          <li>Plan limits and subscription status.</li>
          <li>Basic activity needed for security, debugging, and fair usage.</li>
        </ul>
        <p className="mb-6 leading-relaxed">We do not sell this data.</p>

        <h3 className="text-xl font-semibold text-amber-100 mt-6 mb-3">Technical and Security Data</h3>
        <p className="mb-4 leading-relaxed">
          Like most online services, we may collect technical data such as:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>IP address.</li>
          <li>Browser type.</li>
          <li>Device information.</li>
          <li>Pages or features accessed.</li>
          <li>Timestamps.</li>
          <li>Error logs.</li>
          <li>Security and authentication logs.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          We use this information to keep the service secure, troubleshoot issues, prevent abuse, and improve reliability.
        </p>

        <h3 className="text-xl font-semibold text-amber-100 mt-6 mb-3">Payment and Billing Information</h3>
        <p className="mb-6 leading-relaxed">
          Payments are processed by our payment partner, Dodo Payments.
        </p>
        <p className="mb-6 leading-relaxed">
          We do not store full credit card numbers or complete payment credentials on our own servers. Dodo Payments may collect and process payment details, billing information, tax information, transaction records, and other information needed to process payments and manage subscriptions.
        </p>
        <p className="mb-4 leading-relaxed">We may store limited billing-related information such as:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Subscription status.</li>
          <li>Plan type.</li>
          <li>Payment status.</li>
          <li>Customer or subscription ID.</li>
          <li>Invoice or transaction references.</li>
          <li>Billing cycle information.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">3. How We Use Your Information</h2>
        <p className="mb-4 leading-relaxed">We use your information to:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Create and manage your account.</li>
          <li>Provide the Moment of Truth platform.</li>
          <li>Import, sync, manage, and display reviews.</li>
          <li>Create public Wall of Love pages and embeddable widgets.</li>
          <li>Verify company or review ownership where needed.</li>
          <li>Enforce free and paid plan limits.</li>
          <li>Process subscriptions and billing.</li>
          <li>Provide support.</li>
          <li>Send service-related emails.</li>
          <li>Improve performance, security, and reliability.</li>
          <li>Prevent fraud, abuse, spam, and unauthorized access.</li>
          <li>Comply with legal obligations.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">4. Public Review Pages and Widgets</h2>
        <p className="mb-6 leading-relaxed">
          When you publish reviews through Moment of Truth, the published content may be visible to anyone who visits your public Wall of Love page or sees your embedded widgets.
        </p>
        <p className="mb-4 leading-relaxed">Publicly visible content may include:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Review text.</li>
          <li>Star ratings.</li>
          <li>Reviewer names.</li>
          <li>Reviewer photos.</li>
          <li>Reviewer job titles.</li>
          <li>Company names.</li>
          <li>Review source/platform.</li>
          <li>Your company name, logo, and public page username.</li>
          <li>Badges or trust indicators enabled on your page.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          You are responsible for choosing what to publish and for ensuring you have the necessary rights, permissions, and approvals to display that content publicly.
        </p>
        <p className="mb-6 leading-relaxed">
          If you remove or unpublish content from Moment of Truth, it may no longer appear on your public page or widgets, but cached copies may remain temporarily in browsers, search engines, social previews, or third-party systems outside our control.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">5. Third-Party Review Platforms</h2>
        <p className="mb-6 leading-relaxed">
          Moment of Truth may allow you to import or sync reviews from third-party platforms such as Google, YouTube, or other supported sources.
        </p>
        <p className="mb-6 leading-relaxed">
          When you connect, import, or sync content from a third-party platform, we process the data needed to provide that feature.
        </p>
        <p className="mb-6 leading-relaxed">
          Third-party platforms have their own terms and privacy policies. We are not responsible for the privacy practices, availability, accuracy, or policies of those third-party platforms.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">6. Service Providers</h2>
        <p className="mb-6 leading-relaxed">
          We use trusted third-party service providers to operate Moment of Truth.
        </p>
        <p className="mb-4 leading-relaxed">These may include:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Supabase, for database, authentication, storage, and backend infrastructure.</li>
          <li>Dodo Payments, for payment processing, billing, subscriptions, taxes, and transaction handling.</li>
          <li>Hosting, email, analytics, logging, security, or infrastructure providers that help us run the service.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          These providers may process your information only as needed to provide their services to us, comply with law, or protect their systems.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">7. Data Sharing</h2>
        <p className="mb-6 leading-relaxed">We do not sell your personal data.</p>
        <p className="mb-4 leading-relaxed">We may share information only in limited situations, such as:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>With service providers needed to operate Moment of Truth.</li>
          <li>With Dodo Payments to process payments and subscriptions.</li>
          <li>With infrastructure providers such as Supabase to store and manage data.</li>
          <li>When required by law, regulation, legal process, or government request.</li>
          <li>To protect the rights, safety, security, or integrity of Moment of Truth, our users, or others.</li>
          <li>In connection with a business transfer, merger, acquisition, restructuring, or sale of assets.</li>
          <li>With your consent or at your direction.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">8. Emails and Communications</h2>
        <p className="mb-4 leading-relaxed">We may send you:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Account-related emails.</li>
          <li>Login or security notifications.</li>
          <li>Billing and subscription emails.</li>
          <li>Product updates.</li>
          <li>Support replies.</li>
          <li>Important legal or policy notices.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          You may unsubscribe from non-essential marketing communications where available. However, you may still receive important transactional, billing, security, and service-related emails.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">9. Cookies and Similar Technologies</h2>
        <p className="mb-4 leading-relaxed">Moment of Truth may use cookies or similar technologies to:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Keep you logged in.</li>
          <li>Secure your session.</li>
          <li>Remember preferences.</li>
          <li>Operate the dashboard.</li>
          <li>Improve performance and reliability.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          At this stage, we aim to keep tracking minimal. If we later add analytics, advertising pixels, or similar tracking tools, we may update this Privacy Policy.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">10. Data Retention</h2>
        <p className="mb-6 leading-relaxed">
          We keep your information for as long as needed to provide Moment of Truth, comply with legal obligations, resolve disputes, prevent abuse, and enforce our agreements.
        </p>
        <p className="mb-4 leading-relaxed">
          If you delete your account or request deletion, we will delete or anonymize your personal information where reasonably possible, unless we need to retain certain information for:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Legal compliance.</li>
          <li>Billing and tax records.</li>
          <li>Security and fraud prevention.</li>
          <li>Dispute resolution.</li>
          <li>Backup and recovery processes.</li>
          <li>Legitimate business records.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          Published review content may also remain temporarily visible through caches, backups, search engines, embeds, or third-party systems outside our direct control.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">11. Data Security</h2>
        <p className="mb-6 leading-relaxed">
          We use reasonable technical and organizational measures to protect your information.
        </p>
        <p className="mb-4 leading-relaxed">These may include:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Secure authentication.</li>
          <li>Access controls.</li>
          <li>Encrypted connections where applicable.</li>
          <li>Infrastructure security controls.</li>
          <li>Database and storage protections.</li>
          <li>Monitoring for abuse or unauthorized access.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          However, no method of online transmission or storage is 100% secure. We cannot guarantee absolute security.
        </p>
        <p className="mb-6 leading-relaxed">
          You are responsible for keeping your login credentials safe and notifying us if you suspect unauthorized access to your account.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">12. International Data Transfers</h2>
        <p className="mb-6 leading-relaxed">
          Moment of Truth may use service providers that process or store data in countries other than where you are located.
        </p>
        <p className="mb-6 leading-relaxed">
          By using Moment of Truth, you understand that your information may be transferred to and processed in other countries where our service providers operate.
        </p>
        <p className="mb-6 leading-relaxed">
          We take reasonable steps to ensure that such processing is handled with appropriate safeguards.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">13. Your Rights</h2>
        <p className="mb-4 leading-relaxed">
          Depending on where you live, you may have rights related to your personal data, including the right to:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Access the personal data we hold about you.</li>
          <li>Correct inaccurate or incomplete information.</li>
          <li>Request deletion of your personal data.</li>
          <li>Withdraw consent where processing is based on consent.</li>
          <li>Object to or restrict certain processing.</li>
          <li>Request a copy of your data where applicable.</li>
          <li>Raise a complaint or grievance.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          To exercise these rights, contact us at{' '}
          <a href="mailto:help@momentoftruth.io" className="text-orange-300 hover:text-orange-200 underline underline-offset-4">
            help@momentoftruth.io
          </a>.
        </p>
        <p className="mb-6 leading-relaxed">
          We may need to verify your identity before processing certain requests.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">14. Children&apos;s Privacy</h2>
        <p className="mb-6 leading-relaxed">
          Moment of Truth is intended for business users and is not directed to children.
        </p>
        <p className="mb-6 leading-relaxed">
          You must be at least 18 years old to create an account. We do not knowingly collect personal data from children. If we learn that we have collected personal data from a child without appropriate consent, we will take reasonable steps to delete it.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">15. Your Responsibilities</h2>
        <p className="mb-6 leading-relaxed">
          You are responsible for the content you import, sync, upload, approve, publish, or display through Moment of Truth.
        </p>
        <p className="mb-4 leading-relaxed">
          This includes ensuring that you have the necessary rights, permissions, and approvals to display:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2 text-zinc-400">
          <li>Reviews.</li>
          <li>Testimonials.</li>
          <li>Customer names.</li>
          <li>Customer photos.</li>
          <li>Job titles.</li>
          <li>Company names.</li>
          <li>Logos.</li>
          <li>Ratings.</li>
          <li>Review content from third-party platforms.</li>
        </ul>
        <p className="mb-6 leading-relaxed">
          You are also responsible for ensuring that your use of Moment of Truth complies with privacy, data protection, advertising, consumer protection, and other laws that apply to your business.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">16. Changes to This Privacy Policy</h2>
        <p className="mb-6 leading-relaxed">
          We may update this Privacy Policy from time to time.
        </p>
        <p className="mb-6 leading-relaxed">
          If we make material changes, we will make reasonable efforts to notify you, such as by email, dashboard notice, or website update.
        </p>
        <p className="mb-6 leading-relaxed">
          Your continued use of Moment of Truth after the updated Privacy Policy becomes effective means you accept the changes.
        </p>

        <h2 className="text-2xl font-semibold text-amber-100 mt-8 mb-4">17. Contact Us</h2>
        <p className="mb-6 leading-relaxed">
          If you have questions about this Privacy Policy or your data, contact us at:
        </p>
        <p className="mb-2 leading-relaxed">
          <strong>Email:</strong>{' '}
          <a href="mailto:help@momentoftruth.io" className="text-orange-300 hover:text-orange-200 underline underline-offset-4">
            help@momentoftruth.io
          </a>
        </p>
        <p className="mb-6 leading-relaxed">
          <strong>Website:</strong>{' '}
          <a href="https://momentoftruth.io" target="_blank" rel="noreferrer" className="text-orange-300 hover:text-orange-200 underline underline-offset-4">
            https://momentoftruth.io
          </a>
        </p>
      </div>
    </div>
  );
};

export default Privacy;